import { on } from '@ngrx/store';
import { ActionStatus } from '@pixacare/shared';
import { ActionObjectType } from '@shared/models/sync/action-object-type';
import { SyncAction } from '@shared/models/sync/sync-action';
import { SyncActionState } from '@shared/models/sync/sync-action-state';
import { groupSessionsBySequence } from '@shared/store/video-uploads/utils/group-sessions-by-sequence';
import { videoUploadsActions } from '@shared/store/video-uploads/video-uploads.actions';
import { toSequenceParentKey } from '../../../video-uploads/utils/to-sequence-parent-key';
import { SyncState } from '../../sync.state';
import { toRefKey } from '../../utils/to-ref-key';

// The video-uploads producer of the generic sync slice: responds to the existing video-upload
// actions directly (one writer, one tick — no double-write bridge). The sync child carries only
// identity + state; the transport detail (title, thumbnail, preview, date) stays on the
// video-uploads slice, read at resolve time. The parent id is the sequence's
// `${clientCode}:${sequenceId}` key — a video id is globally unique (uuid / session token).
const videoAction = (input: {
  clientCode: string;
  sequenceId: number;
  videoId: string;
  fileName: string;
  title: string;
  status: ActionStatus;
  syncedBytes: number;
  totalBytes: number;
}): SyncAction => ({
  object: {
    type: ActionObjectType.VIDEO,
    id: input.videoId,
    label: input.fileName,
    parent: {
      type: ActionObjectType.SEQUENCE,
      id: toSequenceParentKey({ clientCode: input.clientCode, sequenceId: input.sequenceId }),
      label: input.title,
    },
  },
  state: { status: input.status, progress: { done: input.syncedBytes, total: input.totalBytes } },
});

const videoStoreKey = (videoId: string): string => toRefKey({ type: ActionObjectType.VIDEO, id: videoId });

const sequenceParentKey = (clientCode: string, sequenceId: number): string =>
  toRefKey({ type: ActionObjectType.SEQUENCE, id: toSequenceParentKey({ clientCode, sequenceId }) });

const upsert = (state: SyncState, action: SyncAction): SyncState => ({
  ...state,
  [toRefKey(action.object)]: action,
});

const patchVideo = (state: SyncState, videoId: string, patch: Partial<SyncActionState>): SyncState => {
  const key = videoStoreKey(videoId);
  const action = state[key];
  if (!action) {
    return state;
  }
  return { ...state, [key]: { ...action, state: { ...action.state, ...patch } } };
};

export const videoUploadsSyncHandlers = [
  on(videoUploadsActions.uploadSequenceVideos, (state, { clientCode, sequenceId, title, videos }): SyncState =>
    videos.reduce(
      (next, video) =>
        upsert(
          next,
          videoAction({
            clientCode,
            sequenceId,
            videoId: video.videoId,
            fileName: video.file.name,
            title,
            status: ActionStatus.WAITING,
            syncedBytes: 0,
            totalBytes: video.file.size,
          }),
        ),
      state,
    ),
  ),

  // Hydrate the server's ongoing sessions as interrupted children. Skips any sequence already
  // tracked, so a live upload in progress is never duplicated or clobbered by hydration.
  on(videoUploadsActions.loadUploadSessionsSuccess, (state, { clientCode, sessions }): SyncState => {
    const trackedParents = new Set(Object.values(state).map((action) => toRefKey(action.object.parent)));
    return Object.entries(groupSessionsBySequence(sessions)).reduce((next, [sequenceId, sequenceSessions]) => {
      const numericSequenceId = Number(sequenceId);
      if (trackedParents.has(sequenceParentKey(clientCode, numericSequenceId))) {
        return next;
      }
      return sequenceSessions.reduce(
        (seeded, session) =>
          upsert(
            seeded,
            videoAction({
              clientCode,
              sequenceId: numericSequenceId,
              videoId: session.sessionToken,
              fileName: session.originalFileName,
              title: '',
              status: ActionStatus.INTERRUPTION,
              syncedBytes: 0,
              totalBytes: session.fileSize,
            }),
          ),
        next,
      );
    }, state);
  }),

  on(videoUploadsActions.resumeVideoUpload, (state, { videoId }): SyncState =>
    patchVideo(state, videoId, {
      status: ActionStatus.WAITING,
      error: undefined,
      progress: { done: 0, total: 0 },
      remainingSeconds: undefined,
    }),
  ),

  // The action only carries the freshly synced bytes; the total (the file size) was seeded when the
  // video was queued, so keep it from the child's last-known progress.
  on(videoUploadsActions.updateVideoUploadProgress, (state, { videoId, syncedBytes, remainingSeconds }): SyncState => {
    const existing = state[videoStoreKey(videoId)];
    const total = existing?.state.progress.total ?? 0;
    return patchVideo(state, videoId, {
      status: ActionStatus.IN_PROGRESS,
      progress: { done: syncedBytes, total },
      remainingSeconds,
    });
  }),

  // Snap the bar to 100% even if the last progress tick never arrived — from the child's own
  // last-known total, no cross-slice read.
  on(videoUploadsActions.videoUploadSuccess, (state, { videoId }): SyncState => {
    const existing = state[videoStoreKey(videoId)];
    const total = existing?.state.progress.total ?? 0;
    const done = total || (existing?.state.progress.done ?? 0);
    return patchVideo(state, videoId, {
      status: ActionStatus.SUCCESS,
      progress: { done, total },
      remainingSeconds: undefined,
    });
  }),

  on(videoUploadsActions.videoUploadError, (state, { videoId, error }): SyncState =>
    patchVideo(state, videoId, { status: ActionStatus.ERROR, error }),
  ),

  on(videoUploadsActions.removeVideoUpload, (state, { videoId }): SyncState => {
    const next = { ...state };
    delete next[videoStoreKey(videoId)];
    return next;
  }),
];
